import { Wizard } from 'react-use-wizard';

import { PREMIUM_BUDGET_STEP_ID, PAYMENT_PREFERENCE_STEP_ID, OPTIONS_STEP_ID } from './config';
import { GuidedPlanningProvider, useGuidedPlanningFlatStepConfigs } from './context';
import { GuidedPlanningHeader } from './components/guided-planning-header';
import { WizardAnimatedStep } from './components/animated-step';
import {
  FundingPlanOverviewStep,
  AssetSelectionStep,
  AssessmentIntroStep,
  AssessmentAccountsStep,
  AssessmentProjectionStep,
  PreferencesDeathBenefitsStep,
  PreferencesPaymentStep,
  PreferencesPremiumStep,
  PreferencesBenefitStructureStep,
  OptionsStep,
  QuotesLoadingStep,
  QuotesSelectionStep,
  QuotesFundingStep,
  PlanComparisonStep,
} from './components/steps';
import { useWizardStepSync } from './hooks';

interface GuidedFinancialPlanningPageProps {
  clientId: string;
}

function GuidedPlanningWizard() {
  const { startIndex, onStepChange } = useWizardStepSync();
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();
  const includePremiumBudget = flatStepConfigs.some(s => s.id === PREMIUM_BUDGET_STEP_ID);
  const includePaymentPreference = flatStepConfigs.some(s => s.id === PAYMENT_PREFERENCE_STEP_ID);
  const includeOptions = flatStepConfigs.some(s => s.id === OPTIONS_STEP_ID);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Wizard
        startIndex={startIndex}
        header={<GuidedPlanningHeader />}
        onStepChange={onStepChange}
        wrapper={<WizardAnimatedStep />}
      >
        <FundingPlanOverviewStep />
        <AssetSelectionStep />
        <AssessmentIntroStep />
        <AssessmentAccountsStep />
        <AssessmentProjectionStep />
        <PreferencesDeathBenefitsStep />
        {includePaymentPreference ? <PreferencesPaymentStep /> : null}
        <PreferencesBenefitStructureStep />
        {includeOptions ? <OptionsStep /> : null}
        {includePremiumBudget ? <PreferencesPremiumStep /> : null}
        <QuotesLoadingStep />
        <QuotesSelectionStep />
        <QuotesFundingStep />
        <PlanComparisonStep />
      </Wizard>
    </div>
  );
}

export function GuidedFinancialPlanningPage({ clientId }: GuidedFinancialPlanningPageProps) {
  return (
    <GuidedPlanningProvider clientId={clientId}>
      <GuidedPlanningWizard />
    </GuidedPlanningProvider>
  );
}
